import { 
  Phone, 
  MessageCircle, 
  MessageSquare, 
  Brain, 
  Bot, 
  Workflow, 
  Database, 
  ShoppingCart, 
  Server,
  ArrowRight,
  Target,
  HeadphonesIcon,
  CheckCircle2,
  RefreshCw,
  Upload,
  Download,
  Globe,
  User,
  Building2,
  Handshake,
  Truck,
  GraduationCap,
  FileText,
  FileSpreadsheet,
  Image,
  Video,
  Cloud
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/hooks/useLanguage";

const integratedChannels = [
  { icon: Phone, labelKey: "voice" },
  { icon: MessageCircle, labelKey: "chat" },
  { icon: MessageSquare, labelKey: "whatsapp" },
];

const platformCapabilities = [
  { icon: Brain, labelKey: "knowledge" },
  { icon: Bot, labelKey: "aiAgents" },
  { icon: Workflow, labelKey: "automation" },
];

const systemIntegrations = [
  { icon: Globe, labelKey: "website" },
  { icon: Database, labelKey: "crm" },
  { icon: ShoppingCart, labelKey: "shop" },
  { icon: Server, labelKey: "erp" },
];

const dataSources = [
  { icon: Globe, label: "WWW" },
  { icon: FileText, label: "PDF" },
  { icon: FileSpreadsheet, label: "XLS" },
  { icon: FileText, label: "CSV" },
  { icon: Database, label: "SQL" },
  { icon: Image, label: "PIC" },
  { icon: Video, label: "Video" },
  { icon: Cloud, label: "Cloud" },
];

const externalUsersConfig = [
  { icon: User, labelKey: "b2c" },
  { icon: Building2, labelKey: "b2b" },
  { icon: Handshake, labelKey: "partner" },
  { icon: Truck, labelKey: "supplier" },
];

const internalUsersConfig = [
  { icon: HeadphonesIcon, labelKey: "support" },
  { icon: GraduationCap, labelKey: "employees" },
];

const journeyPhasesConfig = [
  { labelKey: "marketing", icon: Target, color: "bg-chathero-mint" },
  { labelKey: "sales", icon: ShoppingCart, color: "bg-chathero-orange" },
  { labelKey: "commerce", icon: ShoppingCart, color: "bg-secondary" },
  { labelKey: "service", icon: HeadphonesIcon, color: "bg-chathero-mint" },
];

const WhatIsChatheroSection = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const { t, tArray } = useLanguage();

  const results = tArray('whatIsChathero.results');

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className="py-16 md:py-24 bg-secondary/30 overflow-hidden"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div 
            className={cn(
              "text-center mb-12 md:mb-16 transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t('whatIsChathero.tag')}</Badge>
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">
              {t('whatIsChathero.title')}
            </h2>
            <p className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
              {t('whatIsChathero.titleLine2')}
            </p>
            <p className="text-xl font-semibold text-chathero-mint">
              {t('whatIsChathero.subtitle')}
            </p>
          </div>

          {/* Three-Pillar Flow - Desktop */}
          <div className="hidden md:flex items-stretch justify-center gap-3 lg:gap-4 mb-16">
            {/* Zielgruppen Column */}
            <div 
              className={cn(
                "flex-1 max-w-[220px] transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <div className="bg-card rounded-2xl p-6 border border-border h-full flex flex-col shadow-sm">
                {/* Externe Nutzer */}
                <p className="text-xs font-semibold text-primary uppercase tracking-wider text-center mb-3 mt-2">
                  {t('whatIsChathero.labels.extern')}
                </p>
                <div className="flex justify-center gap-3 mb-3">
                  {externalUsersConfig.map((user, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-1">
                      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                        <user.icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-[10px] text-muted-foreground">{t(`whatIsChathero.externalUsers.${user.labelKey}`)}</span>
                    </div>
                  ))}
                </div>
                
                <Separator className="my-2" />
                
                {/* Interne Nutzer */}
                <p className="text-xs font-semibold text-primary uppercase tracking-wider text-center mb-3">
                  {t('whatIsChathero.labels.intern')}
                </p>
                <div className="flex justify-center gap-3">
                  {internalUsersConfig.map((user, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-1">
                      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                        <user.icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-[10px] text-muted-foreground">{t(`whatIsChathero.internalUsers.${user.labelKey}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Kompakter Sync-Flow Kunden <-> Plattform */}
            <div 
              className={cn(
                "transition-all duration-500 flex flex-col items-center gap-1 self-center",
                isVisible ? "opacity-100 scale-100" : "opacity-0 scale-0"
              )}
              style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}
            >
              <span className="text-xs font-semibold text-chathero-orange">
                {t('whatIsChathero.labels.requests')}
              </span>
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <RefreshCw 
                  className="w-5 h-5 text-primary animate-spin" 
                  style={{ animationDuration: '3s' }} 
                />
              </div>
              <span className="text-xs font-semibold text-chathero-mint">
                {t('whatIsChathero.labels.responses')}
              </span>
            </div>

            {/* Chathero Plattform Column - Highlighted */}
            <div 
              className={cn(
                "flex-1 max-w-xl transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              )}
              style={{ transitionDelay: isVisible ? "300ms" : "0ms" }}
            >
              <div className="bg-chathero-mint/10 rounded-2xl p-6 border-2 border-chathero-mint/30 shadow-lg relative h-full flex flex-col">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-chathero-mint text-primary px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                  Chathero
                </div>
                <h3 className="text-xl font-bold text-primary text-center mb-4 mt-2">
                  AI Agent Plattform
                </h3>
                
                {/* Integrated Channels */}
                <div className="bg-chathero-mint/10 rounded-xl p-4 mb-4">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wider text-center mb-3">
                    {t('whatIsChathero.labels.integratedChannels')}
                  </p>
                  <div className="flex justify-center gap-3">
                    {integratedChannels.map((channel, idx) => (
                      <div key={idx} className="flex flex-col items-center gap-1">
                        <div className="w-10 h-10 rounded-full bg-chathero-mint/20 flex items-center justify-center">
                          <channel.icon className="w-5 h-5 text-primary" />
                        </div>
                        <span className="text-[10px] text-muted-foreground">{t(`whatIsChathero.channels.${channel.labelKey}`)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Platform Capabilities */}
                <div className="flex justify-center gap-3 mb-2">
                  {platformCapabilities.map((cap, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-1">
                      <div className="w-11 h-11 rounded-full bg-chathero-mint/20 flex items-center justify-center">
                        <cap.icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-[10px] text-muted-foreground">{t(`whatIsChathero.capabilities.${cap.labelKey}`)}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex-1" />
                
                {/* Knowledge-Base Highlight */}
                <div className="bg-chathero-mint/10 rounded-lg p-3 mt-3">
                  <div className="flex items-center justify-center gap-2 text-sm text-primary">
                    <RefreshCw 
                      className="w-4 h-4 text-primary animate-spin" 
                      style={{ animationDuration: '3s' }} 
                    />
                    <span>{t('whatIsChathero.labels.knowledgeBase')}</span>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-1">
                    {t('whatIsChathero.labels.autoSync')}
                  </p>
                </div>
              </div>
            </div>

            {/* Sync Icon - Desktop */}
            <div 
              className={cn(
                "transition-all duration-500 flex flex-col items-center gap-2 self-center",
                isVisible ? "opacity-100 scale-100" : "opacity-0 scale-0"
              )}
              style={{ transitionDelay: isVisible ? "500ms" : "0ms" }}
            >
              <span className="text-xs font-semibold text-chathero-orange text-center">
                {t('whatIsChathero.labels.data')}
              </span>
              
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <RefreshCw 
                  className="w-5 h-5 text-primary animate-spin" 
                  style={{ animationDuration: '3s' }} 
                />
              </div>
              
              <span className="text-xs font-semibold text-chathero-mint">
                API
              </span>
            </div>

            {/* Systeme Column */}
            <div 
              className={cn(
                "flex-1 max-w-[220px] transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
              )}
              style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}
            >
              <div className="bg-card rounded-2xl p-6 border border-border h-full flex flex-col shadow-sm">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider text-center mb-3 mt-2">
                  {t('whatIsChathero.labels.integrations')}
                </p>
                <div className="flex justify-center gap-3 mb-4">
                  {systemIntegrations.map((system, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-1">
                      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                        <system.icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-[10px] text-muted-foreground">{t(`whatIsChathero.systems.${system.labelKey}`)}</span>
                    </div>
                  ))}
                </div>
                
                <Separator className="my-3" />
                
                <p className="text-xs font-semibold text-primary uppercase tracking-wider text-center mb-3">
                  {t('whatIsChathero.labels.dataSources')}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {dataSources.map((source, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-1">
                      <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
                        <source.icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-[9px] text-muted-foreground">{source.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Three-Pillar Flow - Mobile (Vertical) */}
          <div className="md:hidden flex flex-col items-center gap-4 mb-12">
            {/* Zielgruppen - Mobile */}
            <div 
              className={cn(
                "w-full max-w-xs transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <div className="bg-card rounded-xl p-4 border border-border flex flex-col shadow-sm">
                {/* Externe - Mobile */}
                <p className="text-[9px] font-semibold text-primary uppercase tracking-wider text-center mb-2 mt-1">
                  {t('whatIsChathero.labels.extern')}
                </p>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {externalUsersConfig.map((user, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-0.5">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                        <user.icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-[8px] text-muted-foreground">{t(`whatIsChathero.externalUsers.${user.labelKey}`)}</span>
                    </div>
                  ))}
                </div>
                
                <Separator className="my-2" />
                
                {/* Interne - Mobile */}
                <p className="text-[9px] font-semibold text-primary uppercase tracking-wider text-center mb-2">
                  {t('whatIsChathero.labels.intern')}
                </p>
                <div className="flex justify-center gap-4">
                  {internalUsersConfig.map((user, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-0.5">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                        <user.icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-[8px] text-muted-foreground">{t(`whatIsChathero.internalUsers.${user.labelKey}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Kompakter Sync-Flow - Mobile */}
            <div 
              className={cn(
                "transition-all duration-500 flex flex-col items-center gap-1 py-2",
                isVisible ? "opacity-100" : "opacity-0"
              )}
              style={{ transitionDelay: isVisible ? "300ms" : "0ms" }}
            >
              <span className="text-xs font-semibold text-chathero-orange">{t('whatIsChathero.labels.requests')}</span>
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                <RefreshCw 
                  className="w-4 h-4 text-primary animate-spin" 
                  style={{ animationDuration: '3s' }} 
                />
              </div>
              <span className="text-xs font-semibold text-chathero-mint">{t('whatIsChathero.labels.responses')}</span>
            </div>

            {/* Plattform - Mobile */}
            <div 
              className={cn(
                "w-full max-w-sm transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}
            >
              <div className="bg-chathero-mint/10 rounded-xl p-5 border-2 border-chathero-mint/30 shadow-lg relative">
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-chathero-mint text-primary px-3 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider">
                  Chathero
                </div>
                <h3 className="text-lg font-bold text-primary text-center mb-3 mt-1">
                  AI Agent Plattform
                </h3>
                
                {/* Integrated Channels - Mobile */}
                <div className="bg-chathero-mint/10 rounded-lg p-3 mb-3">
                  <p className="text-[10px] font-semibold text-primary uppercase tracking-wider text-center mb-2">
                    {t('whatIsChathero.labels.integratedChannels')}
                  </p>
                  <div className="flex justify-center gap-2">
                    {integratedChannels.map((channel, idx) => (
                      <div key={idx} className="flex flex-col items-center gap-0.5">
                        <div className="w-8 h-8 rounded-full bg-chathero-mint/20 flex items-center justify-center">
                          <channel.icon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-[8px] text-muted-foreground">{t(`whatIsChathero.channels.${channel.labelKey}`)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Platform Capabilities - Mobile */}
                <div className="flex justify-center gap-2 mb-2">
                  {platformCapabilities.map((cap, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-0.5">
                      <div className="w-9 h-9 rounded-full bg-chathero-mint/20 flex items-center justify-center">
                        <cap.icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-[8px] text-muted-foreground">{t(`whatIsChathero.capabilities.${cap.labelKey}`)}</span>
                    </div>
                  ))}
                </div>
                
                {/* Knowledge-Base - Mobile */}
                <div className="bg-chathero-mint/10 rounded-lg p-2 mt-2">
                  <div className="flex items-center justify-center gap-2 text-xs text-primary">
                    <RefreshCw 
                      className="w-3 h-3 text-primary animate-spin" 
                      style={{ animationDuration: '3s' }} 
                    />
                    <span>{t('whatIsChathero.labels.knowledgeBase')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bidirektionaler Pfeil - Mobile */}
            <div 
              className={cn(
                "transition-all duration-500 flex flex-col items-center gap-1 py-2",
                isVisible ? "opacity-100" : "opacity-0"
              )}
              style={{ transitionDelay: isVisible ? "500ms" : "0ms" }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-chathero-orange/30 flex items-center justify-center animate-pulse">
                  <Download className="w-4 h-4 text-chathero-orange" />
                </div>
                <span className="text-xs text-chathero-orange">{t('whatIsChathero.labels.knowledgeBase').split(' ')[0]}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-chathero-mint">{t('whatIsChathero.labels.data')}</span>
                <div 
                  className="w-8 h-8 rounded-full bg-chathero-mint/30 flex items-center justify-center animate-pulse"
                  style={{ animationDelay: '1s' }}
                >
                  <Upload className="w-4 h-4 text-chathero-mint" />
                </div>
              </div>
            </div>

            {/* Systeme - Mobile */}
            <div 
              className={cn(
                "w-full max-w-sm transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
            >
              <div className="bg-card rounded-xl p-5 border border-border shadow-sm">
                <p className="text-[9px] font-semibold text-primary uppercase tracking-wider text-center mb-2 mt-1">
                  {t('whatIsChathero.labels.integrations')}
                </p>
                <div className="flex justify-center gap-3 mb-2">
                  {systemIntegrations.map((system, idx) => (
                    <div 
                      key={idx}
                      className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center"
                    >
                      <system.icon className="w-5 h-5 text-primary" />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground text-center mb-3">
                  {t('whatIsChathero.systems.website')}, {t('whatIsChathero.systems.crm')}, {t('whatIsChathero.systems.shop')}, {t('whatIsChathero.systems.erp')}
                </p>
                
                <Separator className="my-2" />
                
                <p className="text-[9px] font-semibold text-primary uppercase tracking-wider text-center mb-2">
                  {t('whatIsChathero.labels.dataSources')}
                </p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {dataSources.map((source, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-0.5">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                        <source.icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-[7px] text-muted-foreground">{source.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Customer Journey Strip */}
          <div 
            className={cn(
              "mb-12 transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
            style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
          >
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-6 text-center">
              {t('whatIsChathero.journeyTitle')}
            </p>
            <div className="relative">
              {/* Connection line */}
              <div className="hidden md:block absolute top-1/2 left-[10%] right-[10%] h-1 bg-gradient-to-r from-chathero-mint via-chathero-orange to-chathero-mint transform -translate-y-1/2 rounded-full opacity-30" />
              
              <div className="flex flex-wrap md:flex-nowrap justify-center gap-3 md:gap-0">
                {journeyPhasesConfig.map((phase, index) => (
                  <div
                    key={index}
                    className={cn(
                      "relative z-10 flex-1 max-w-[140px] md:max-w-none transition-all",
                      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                    )}
                    style={{ 
                      transitionDelay: isVisible ? `${700 + index * 100}ms` : "0ms",
                      transitionDuration: "500ms"
                    }}
                  >
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 ${phase.color} rounded-full flex items-center justify-center mb-2`}>
                        <phase.icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-primary">
                        {t(`whatIsChathero.journeyPhases.${phase.labelKey}`)}
                      </span>
                    </div>
                    {index < journeyPhasesConfig.length - 1 && (
                      <div className="hidden md:block absolute top-5 left-full w-full">
                        <ArrowRight className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div 
            className={cn(
              "bg-card rounded-2xl p-6 md:p-8 border border-border shadow-sm transition-all duration-700 ease-out",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
            style={{ transitionDelay: isVisible ? "900ms" : "0ms" }}
          >
            <h3 className="text-lg font-semibold text-primary mb-6 text-center">
              {t('whatIsChathero.resultTitle')}
            </h3>
            <div className="flex flex-wrap justify-center gap-4 md:gap-8">
              {results.map((result, index) => (
                <div 
                  key={index} 
                  className={cn(
                    "flex items-center gap-2 text-primary transition-all",
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  )}
                  style={{ 
                    transitionDelay: isVisible ? `${1000 + index * 100}ms` : "0ms",
                    transitionDuration: "400ms"
                  }}
                >
                  <CheckCircle2 className="w-5 h-5 text-chathero-mint flex-shrink-0" />
                  <span className="font-medium text-sm md:text-base">{result}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatIsChatheroSection;
